// Markdown rendering for chat bubbles.
//
// Uses `marked` for GFM-compliant parsing (code fences, inline code,
// lists, links, tables, strikethrough, task lists) and post-processes
// the output to lock down link schemes — anchors that aren't
// http/https/mailto get their href stripped so a malicious LLM
// response can't smuggle a javascript: URL in.
//
// We deliberately do NOT pre-escape `<` / `>` before parsing, because
// marked already escapes them by default when `gfm: true` and
// `breaks: true`. The threat model is "an LLM accidentally emits a
// `<script>` tag inside its reply" — extremely unlikely in normal
// chat, but the link-scheme guard is the practical mitigation if a
// jailbreak ever flips that.
//
// Strips leading whitespace so Qwen-family models' template-padded
// newlines don't render as blank lines above the visible text.

import { marked } from "marked";

// One-time marked config. Defaults are close to what we want — GFM
// auto-detect handles fences/tables/strikethrough; we add `breaks:true`
// so a single \n inside a paragraph renders as <br> (matches the
// pre-wrap behavior the old client had).
marked.setOptions({
  gfm: true,
  breaks: true,
});

const SAFE_HREF = /^(https?:|mailto:|tel:|#)/i;

function sanitizeLinks(html: string): string {
  // Cheap regex pass over rendered anchor tags. Replaces unsafe hrefs
  // with `#` and stamps target=_blank + rel=noopener so external links
  // don't keep a window.opener reference back to the chat tab.
  return html.replace(
    /<a\s+([^>]*?)href="([^"]*)"([^>]*)>/gi,
    (_m, before, href, after) => {
      const safe = SAFE_HREF.test(href.trim()) ? href : "#";
      const cleanedBefore = before.replace(/\s*(target|rel)="[^"]*"/gi, "");
      const cleanedAfter = after.replace(/\s*(target|rel)="[^"]*"/gi, "");
      return `<a ${cleanedBefore}href="${safe}"${cleanedAfter} target="_blank" rel="noopener noreferrer">`;
    },
  );
}

export function formatMd(s: string): string {
  if (!s) return "";
  // Strip the [continue] voice-handoff marker the model emits to signal
  // "expecting a reply" — display surfaces shouldn't show it. See the
  // system prompt addendum in agent_loop.mjs.
  const stripped = s.replace(/\s*\[continue\]\s*$/i, "");
  const trimmed = stripped.trim();
  if (!trimmed) return "";
  const rendered = marked.parse(trimmed, { async: false }) as string;
  return sanitizeLinks(rendered);
}
